// referEarnApi.ts
import axios, { RawAxiosRequestHeaders } from 'axios';
import Cookies from 'universal-cookie';
import General from '../../../../general';
import config from '../../../../config';

export interface UserAuth {
  userId: string;
  token: string;
}

export interface ApiHeaders {
  apiKey: string;
  userId?: string;
  token?: string;
}

interface GetUserAuthParams {
  apiKey: string;
  entityId: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  entityAuthenticationToken?: string;
}



interface ExternalLoginResponse {
  userId: string;
  token: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  userId: string;
  token: string;
}

const platforms = {
    x: (text: string) => `https://x.com/intent/post?text=${encodeURIComponent(text)}`,
    telegram: (text: string) => `https://t.me/share/url?url=${encodeURIComponent(text)}`,
    whatsapp: (text: string) => `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
    linkedin: (text: string) => `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`,
    facebook: (text: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(text)}`,
};

export type Platform = keyof typeof platforms;

class ReferEarnApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL = apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
    return {
      'Content-Type': 'application/json',
      ...headers
    };
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    entityAuthenticationToken,
  }: GetUserAuthParams): Promise<UserAuth | null> {
    try {
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken
        };
      }

      if (uniqueUserId) {
        const headers = this.getAxiosHeaders({
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
          ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
        });

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post<ApiResponse<ExternalLoginResponse>>(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );

        if (!response.data?.success) {
          throw new Error('Invalid response from external login');
        }

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken
        };
      }

      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async getReferralCode(
    questId: string,
    entityId: string,
    headers: ApiHeaders,
    variation?: string,
  ) {
    try {
      if (!headers.userId || !headers.token) {
        throw new Error('Missing required headers');
      }

      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const axiosHeaders = this.getAxiosHeaders(headers);
      const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;
      
      const response = await axios.get(request, {
        headers: axiosHeaders
      });

      if (!response.data?.data) {
        throw new Error('No data received from API');
      }

      return response.data.data ;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return "";
    }
  }

  

  shareOnPlatform(text: string, platform: string): void {
    const url = platforms[platform](text);
    window.open(url, '_blank', 'width=600,height=400');
  }

  clearCookies(): void {
    this.cookies.remove("externalUserId", { path: "/" });
    this.cookies.remove("questUserId", { path: "/" });
    this.cookies.remove("questUserToken", { path: "/" });
  }

  isValidUserAuth(userAuth: unknown): userAuth is UserAuth {
    return (
      !!userAuth &&
      typeof (userAuth as UserAuth).userId === 'string' &&
      typeof (userAuth as UserAuth).token === 'string'
    );
  }
}

export default ReferEarnApi;